import { useState, useCallback } from 'react';

export type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

export interface SnackbarState {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
}

export interface UseSnackbarReturn {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  show: (message: string, severity?: SnackbarSeverity) => void;
  close: () => void;
}

export function useSnackbar(autoHideDuration = 6000): UseSnackbarReturn {
  const [state, setState] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const show = useCallback((message: string, severity: SnackbarSeverity = 'success') => {
    setState({
      open: true,
      message,
      severity,
    });
  }, []);

  const showSuccess = useCallback((message: string) => {
    show(message, 'success');
  }, [show]);

  const showError = useCallback((message: string) => {
    show(message, 'error');
  }, [show]);

  const showWarning = useCallback((message: string) => {
    show(message, 'warning');
  }, [show]);

  const showInfo = useCallback((message: string) => {
    show(message, 'info');
  }, [show]);

  const close = useCallback(() => {
    setState(prev => ({ ...prev, open: false }));
  }, []);

  return {
    open: state.open,
    message: state.message,
    severity: state.severity,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    show,
    close,
  };
}

export default useSnackbar;
